import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FORM_MODE } from "../../config/constants";
import { DELIVERY_TYPES } from "../../config/availability";
import Input from "../Shared/Input";
import FormFooter from "../Shared/FormFooter";
import FormWrapper from "../Shared/FormWrapper";
import AlertRed from "../Shared/AlertRed";
import AlertYellow from "../Shared/AlertYellow";
import ErrorMessage from "../Shared/ErrorMessage";
import TextArea from "../Shared/TextArea";
import DurationPicker from "../Shared/DurationPicker";
import PriceInput from "../Shared/PriceInput";
import ImageUpload from "../Shared/ImageUpload";
import SelectableCardGroup from "../Shared/SelectableCardGroup";
import {
  TruckIcon,
  BuildingStorefrontIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import { mapApiError } from "../../utils/formErrorMapper";
import useScrollToFirstError from "../../hooks/useScrollToFirstError";
import {
  getAvailabilities,
  saveAvailability,
} from "../../api/availabilityApi";
import {
  deriveProviderAvailabilityTypes,
  inferDeliveryType,
} from "../../utility/serviceBookability";
import { deriveDistanceUnit } from "../../utility/distanceUnitUtils";
import AvailabilityForm from "../Availability/AvailabilityForm";
import ReenableAvailabilityModal from "../Availability/ReenableAvailabilityModal";
import { useToast } from "../../hooks/useToast";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../hooks/useBookingPage";
import TravelFeeFields from "../BookingLinkConfig/Settings/TravelFeeFields";

const DELIVERY_TYPE_OPTIONS = [
  {
    id: DELIVERY_TYPES.MOBILE,
    title: "You go to the client",
    helper: "You travel to their address.",
    icon: TruckIcon,
  },
  {
    id: DELIVERY_TYPES.FIXED,
    title: "Clients come to you",
    helper: "They visit your location.",
    icon: BuildingStorefrontIcon,
  },
  {
    id: DELIVERY_TYPES.HYBRID,
    title: "Both options",
    helper: "Clients choose how to book.",
    icon: ArrowsRightLeftIcon,
    tooltip:
      "Needs both a mobile and a fixed-location availability — otherwise only the one you have takes bookings.",
  },
];

const warnButtonClass = "font-medium underline hover:opacity-80 disabled:opacity-50";

// Builds the soft-guard warning message + inline action for one delivery type.
// kind: "mobile" | "fixed". Copy uses "mobile availability" / "location".
// Two states only: missing (no disabled records) → in-drawer step-swap create
// (locked to this type); turned off (disabled records exist) → open the
// toggle-list ReenableAvailabilityModal (safe to layer — it's not a form).
const renderAvailabilityWarning = ({ kind, inactiveCount, onSetUp, onReenable }) => {
  const mobile = kind === "mobile";

  // Missing entirely — create it inline (step swap).
  if (!inactiveCount) {
    return mobile ? (
      <>
        Not bookable as mobile yet — you have no mobile availability.{" "}
        <button type="button" onClick={onSetUp} className={warnButtonClass}>
          Add mobile availability
        </button>
      </>
    ) : (
      <>
        Not bookable at your location yet — you haven&rsquo;t set up a location.{" "}
        <button type="button" onClick={onSetUp} className={warnButtonClass}>
          Add a location
        </button>
      </>
    );
  }

  // Turned off — open the toggle-list modal (works for any count).
  return mobile ? (
    <>
      Not bookable as mobile — a mobile availability is turned off.{" "}
      <button type="button" onClick={onReenable} className={warnButtonClass}>
        Turn availability on
      </button>
    </>
  ) : (
    <>
      Not bookable at your location — your location is turned off.{" "}
      <button type="button" onClick={onReenable} className={warnButtonClass}>
        Turn availability on
      </button>
    </>
  );
};

const ServiceForm = ({
  defaultService,
  closeDrawerHandler,
  saveService,
  patchService,
  mode,
  onCancelHandler,
  setIsFormDirty,
  // Lets the host drawer retitle itself while this form swaps to the embedded
  // "set up availability" step (mirrors AppointmentForm's onModalTitleChange).
  onTitleChange,
}) => {
  const maxCharacters = 500;
  const [charCount, setCharCount] = useState(() => {
    if (defaultService?.description) {
      return defaultService.description.length;
    }
    return 0;
  });
  const [banner, setBanner] = useState(null);
  const bannerRef = useRef(null);
  // CREATE-only apply-once guard for the availability-derived delivery-type
  // default (see the useEffect below, after providerHasMobile/providerHasFixed
  // are known).
  const deliveryPreselectedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);
  // Travel-fee base location address for the TravelFeeFields sub-component.
  const [travelFeeAddress, setTravelFeeAddress] = useState(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  // -------------------------------------------------------------------------
  // Service form
  // -------------------------------------------------------------------------
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, dirtyFields },
    setError,
    watch,
  } = useForm({
    defaultValues: {
      name: defaultService?.name ? defaultService.name : "",
      description: defaultService?.description
        ? defaultService.description
        : "",
      duration: defaultService?.duration ? defaultService.duration : 60,
      price: defaultService?.price ? parseFloat(defaultService?.price) : 0,
      imageUrl: defaultService?.imageUrl,
      image: null,
      deliveryType: defaultService?.deliveryType ?? DELIVERY_TYPES.HYBRID,
    },
  });

  const imageFile = watch("image");
  const deliveryType = watch("deliveryType");
  const showTravelFeeBlock =
    deliveryType === DELIVERY_TYPES.MOBILE || deliveryType === DELIVERY_TYPES.HYBRID;

  // -------------------------------------------------------------------------
  // Travel-fee settings form (separate from the service form — saves through
  // updateSettings / /booking-page/settings, NOT the service payload)
  // -------------------------------------------------------------------------
  const { data: bookingPageData } = useBookingPage();
  const { mutate: updateSettings, isPending: isUpdatingSettings } =
    useUpdateSettingsTab();

  const {
    register: registerFee,
    watch: watchFee,
    setValue: setValueFee,
    handleSubmit: handleSubmitFee,
    reset: resetFee,
    formState: { isDirty: isFeeDirty },
  } = useForm({
    defaultValues: {
      travelFeesEnabled: false,
      travelFeeType: "flat",
      travelFeeFlatRate: 0,
      travelFeePerDistance: 0,
      travelFeeDistanceUnit: "km",
      baseLocation: null,
    },
  });

  // Sync fee form when booking page data loads.
  useEffect(() => {
    if (bookingPageData?.data) {
      const d = bookingPageData.data;
      const feeData = {
        travelFeesEnabled: d.travelFeesEnabled || false,
        travelFeeType: d.travelFeeType || "flat",
        travelFeeFlatRate: d.travelFeeFlatRate
          ? parseFloat(d.travelFeeFlatRate)
          : 0,
        travelFeePerDistance: d.travelFeePerDistance
          ? parseFloat(d.travelFeePerDistance)
          : 0,
        travelFeeDistanceUnit: d.travelFeeDistanceUnit || "km",
        baseLocation: d.baseLocation || null,
      };
      resetFee(feeData, { keepDirty: false });
      if (d.baseLocation) {
        setTravelFeeAddress({
          id: d.baseLocation.placeId,
          name: d.baseLocation.address,
        });
      } else {
        setTravelFeeAddress(null);
      }
    }
  }, [bookingPageData, resetFee]);

  const handleFeeAddressSelect = (address) => {
    setTravelFeeAddress(address);
    if (address?.id && address?.name) {
      setValueFee(
        "baseLocation",
        { placeId: address.id, address: address.name },
        { shouldDirty: true }
      );
      setValueFee(
        "travelFeeDistanceUnit",
        deriveDistanceUnit(address.name),
        { shouldDirty: true }
      );
    } else {
      setValueFee("baseLocation", null, { shouldDirty: true });
    }
  };

  const saveTravelFeeSettings = (feeData, mutateOptions) => {
    const formattedData = {
      ...feeData,
      travelFeeFlatRate: feeData.travelFeeFlatRate
        ? parseFloat(feeData.travelFeeFlatRate).toFixed(2)
        : "0.00",
      travelFeePerDistance: feeData.travelFeePerDistance
        ? parseFloat(feeData.travelFeePerDistance).toFixed(2)
        : "0.00",
    };
    updateSettings(formattedData, mutateOptions);
  };

  // -------------------------------------------------------------------------
  // Provider availability — soft guard (non-blocking)
  // -------------------------------------------------------------------------
  const { data: availabilities = [], isSuccess: availabilitiesLoaded } =
    useQuery({
      queryKey: ["availabilities"],
      queryFn: getAvailabilities,
      staleTime: 5 * 60 * 1000,
    });

  // Derive what availability types the provider currently has (shared with
  // the services-page attention UI). Also surfaces the full disabled-record
  // lists of a missing type so the warning can offer "turn availability on"
  // (opens the toggle-list modal, any count) instead of prompting a
  // duplicate create.
  const {
    providerHasMobile,
    providerHasFixed,
    inactiveMobileCount,
    inactiveFixedCount,
    inactiveMobileList,
    inactiveFixedList,
  } = deriveProviderAvailabilityTypes(availabilities);

  // CREATE only: once we know what the provider offers, refine the delivery-type
  // default from HYBRID to their single mode. Runs at most once, never after the
  // user has touched the card. shouldDirty:false so this smart default doesn't
  // pre-enable the save footer / confirm-close.
  useEffect(() => {
    if (mode !== FORM_MODE.CREATE) return;
    if (!availabilitiesLoaded) return;
    if (deliveryPreselectedRef.current) return;
    if (dirtyFields.deliveryType) return;

    const inferred = inferDeliveryType(providerHasMobile, providerHasFixed);
    deliveryPreselectedRef.current = true;
    if (inferred !== DELIVERY_TYPES.HYBRID) {
      setValue("deliveryType", inferred, { shouldDirty: false });
    }
  }, [
    mode,
    availabilitiesLoaded,
    providerHasMobile,
    providerHasFixed,
    dirtyFields.deliveryType,
    setValue,
  ]);

  // Gated on isSuccess (not length) so zero-availability providers see the
  // warning too; still avoids a flash before the query resolves.
  const showMobileWarning =
    (deliveryType === DELIVERY_TYPES.MOBILE ||
      deliveryType === DELIVERY_TYPES.HYBRID) &&
    !providerHasMobile &&
    availabilitiesLoaded;

  const showFixedWarning =
    (deliveryType === DELIVERY_TYPES.FIXED ||
      deliveryType === DELIVERY_TYPES.HYBRID) &&
    !providerHasFixed &&
    availabilitiesLoaded;

  // Quick availability setup — an in-drawer STEP SWAP (not a stacked modal).
  // When set, the form early-returns AvailabilityForm inside the same service
  // drawer, so there's only ever one Dialog / one <form> mounted at a time.
  // Stacking a second Modal here froze the inner form (worst on mobile, where
  // the address picker adds a third Dialog) and misrouted the submit to the
  // service form.
  const [availStep, setAvailStep] = useState(null); // "mobile" | "fixed" | null

  // Retitle the host drawer while on the availability step.
  useEffect(() => {
    if (!onTitleChange) return;
    onTitleChange(
      availStep
        ? "Set up availability"
        : mode === FORM_MODE.CREATE
          ? "Create New Service"
          : "Edit Service"
    );
  }, [availStep, mode, onTitleChange]);

  // CREATE mode never reaches the availability form's update/patch paths;
  // stubs satisfy its prop contract on the embedded step.
  const noop = async () => {};

  // Create the availability from the embedded step, then return to the service
  // form. The ["availabilities"] invalidation reruns this form's query so the
  // soft-guard warning clears in place; the in-progress service is untouched.
  const { mutateAsync: mutateQuickSaveAvailability } = useMutation({
    mutationFn: ({ data }) => saveAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availabilities"], exact: true });
      queryClient.invalidateQueries({ queryKey: ["setupProgress"], exact: true });
      toast.success(
        "Availability Created",
        "New availability has been created successfully"
      );
      // Return to the service form and hand its dirty state back atomically
      // (see exitAvailStep — same reasoning, but that helper isn't in scope yet).
      setAvailStep(null);
      setIsFormDirty(isDirty);
    },
  });

  // Which type's toggle-list re-enable modal is open (layered over the
  // service drawer — safe, since it's not a form). null when closed.
  const [reenableKind, setReenableKind] = useState(null); // "mobile" | "fixed" | null

  // -------------------------------------------------------------------------
  // Service form handlers
  // -------------------------------------------------------------------------
  const onRemoveHandler = () => {
    setValue("imageUrl", null, {
      shouldDirty: true,
    });
  };

  const runSubmit = async (data) => {
    if (isSubmitting) return;
    setBanner(null);
    setLastSubmitPayload(data);
    setIsSubmitting(true);

    // The fee save on the CREATE and UPDATE-with-service-changes paths fires
    // fire-and-forget AFTER the drawer has already closed, so a failure can't
    // use the in-form banner. Surface it as a toast instead. (The fee-only
    // path keeps its banner — its drawer is still open.)
    const feeSaveFailedToast = () =>
      toast.error(
        "Travel fee not saved",
        "Your service saved, but the travel fee couldn't be updated. Try editing it again."
      );

    try {
      const formData = new FormData();

      if (mode === FORM_MODE.CREATE) {
        formData.append("name", data.name);
        formData.append("description", data.description || "");
        formData.append("duration", data.duration);
        formData.append("price", data.price);
        formData.append("deliveryType", data.deliveryType);
        if (data.image && data.image[0])
          formData.append("image", data.image[0]);

        await saveService(formData);

        // If travel-fee fields were changed while creating this service,
        // persist them through updateSettings immediately after the service
        // saves. This is provider-level, not per-service, so it fires
        // regardless of which service was created (preserves prior behavior
        // that lived in the now-removed trailing `if (isFeeDirty)` block).
        if (isFeeDirty) {
          handleSubmitFee((feeData) =>
            saveTravelFeeSettings(feeData, { onError: feeSaveFailedToast })
          )();
        }
      } else if (mode === FORM_MODE.UPDATE) {
        const { _id: id } = defaultService;

        if (dirtyFields.name) formData.append("name", data.name);
        if (dirtyFields.description)
          formData.append("description", data.description || "");
        if (dirtyFields.duration)
          formData.append("duration", data.duration.toString());
        if (dirtyFields.price) formData.append("price", data.price);
        if (dirtyFields.imageUrl) formData.append("imageUrl", data.imageUrl);
        if (dirtyFields.image && data.image[0])
          formData.append("image", data.image[0]);
        if (dirtyFields.deliveryType)
          formData.append("deliveryType", data.deliveryType);

        // Gate the PATCH on what was actually appended, not on dirtyFields'
        // key count: a field can be dirty yet append nothing (e.g. the image
        // input is dirty after the user opens then cancels the OS file picker,
        // but `dirtyFields.image && data.image[0]` skips the append). An empty
        // FormData PATCH is the exact false-400 this branch exists to avoid.
        const serviceChanged = [...formData.keys()].length > 0;

        if (serviceChanged) {
          // patchService's onSuccess (ServicePage) toasts "Service Updated"
          // and closes the drawer. Fire the fee save alongside it WITHOUT
          // its own toast/close so we don't double up.
          await patchService({ id, formData });
          if (isFeeDirty) {
            handleSubmitFee((feeData) =>
              saveTravelFeeSettings(feeData, { onError: feeSaveFailedToast })
            )();
          }
        } else if (isFeeDirty) {
          // Fee-only edit: no service PATCH (avoids the false 400). This
          // path owns success feedback + drawer close.
          handleSubmitFee((feeData) =>
            saveTravelFeeSettings(feeData, {
              onSuccess: () => {
                toast.success(
                  "Travel fee updated",
                  "Your travel fee has been saved"
                );
                closeDrawerHandler();
              },
              onError: (err) => {
                const result = mapApiError(err, { setError });
                if (result.handled || result.kind === "auth") return;
                setBanner(result.banner);
              },
            })
          )();
        }
        // else: nothing dirty at all — no-op (drawer stays open).
      }
    } catch (err) {
      const result = mapApiError(err, { setError });
      if (result.handled) return;
      if (result.kind === "auth") return;
      setBanner(result.banner);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (lastSubmitPayload) runSubmit(lastSubmitPayload);
  };

  const onDurationUpdatedHandler = (duration) => {
    setValue("duration", duration, {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    // While the availability step is active, AvailabilityForm owns the drawer's
    // dirty state (via the same setIsFormDirty). Don't let the service form's
    // dirtiness fight it — only sync when the service form is the visible one.
    if (availStep) return;
    setIsFormDirty(isDirty);
  }, [isDirty, setIsFormDirty, availStep]);

  // Leave the availability step and hand the drawer's dirty flag back to the
  // service form in the SAME tick. AvailabilityForm set setIsFormDirty(true) as
  // the user edited it; without this explicit reset the drawer's confirmClose
  // would briefly still read "dirty" from the child until the post-render sync
  // effect above re-runs. Resetting here to the service form's own isDirty makes
  // the handoff atomic instead of relying on effect timing.
  const exitAvailStep = () => {
    setAvailStep(null);
    setIsFormDirty(isDirty);
  };

  useScrollToFirstError(errors);

  // The submit-failure banner renders at the top of the form, but the image
  // field and Save button sit at the bottom — nothing else scrolls the
  // banner into view, so it can appear off-screen. Mirrors
  // useScrollToFirstError's scroll-into-view pattern (smooth, centered).
  useEffect(() => {
    if (!banner) return;
    bannerRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [banner]);

  // Embedded "set up availability" step — swaps the service drawer's content
  // to the availability form (single Dialog, single <form>), then returns.
  // Locked to `availStep`'s type so the user can't accidentally create the
  // other delivery type instead of fixing the one that's missing.
  if (availStep) {
    return (
      <AvailabilityForm
        activeTabGroup={availStep}
        mode={FORM_MODE.CREATE}
        defaultAvailability={null}
        saveAvailability={mutateQuickSaveAvailability}
        updateAvailability={noop}
        patchAvailability={noop}
        closeDrawerHandler={exitAvailStep}
        onCancelHandler={exitAvailStep}
        setIsFormDirty={setIsFormDirty}
        secondaryCaption="Back to service"
        lockMeetMode={true}
      />
    );
  }

  return (
    <>
    <FormWrapper onSubmit={handleSubmit(runSubmit)}>
      {banner ? (
        <div className="mb-4" ref={bannerRef}>
          <AlertRed
            title={banner.title}
            message={banner.message}
            onRetry={handleRetry}
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="col-span-full">
          <Input
            id="name"
            name="name"
            label="Service name"
            placeholder="e.g., Deep clean — 3 bed"
            errormessage={errors?.name?.message}
            {...register("name", {
              onBlur: (e) => {
                setValue("name", e.target.value.trim());
              },
              required: "Service name is required",
            })}
          />
        </div>
        <div className="col-span-full">
          <TextArea
            label="Description"
            optional
            id="description"
            name="description"
            placeholder="What's included, any prep needed, anything clients should know"
            errormessage={errors?.description?.message}
            charCount={charCount}
            maxLength={maxCharacters}
            {...register("description", {
              maxLength: {
                value: maxCharacters,
                message: `Description cannot exceed ${maxCharacters} characters`,
              },
              onBlur: (e) => {
                setValue("description", e.target.value.trim(), {
                  shouldValidate: true,
                });
              },
              onChange: (e) => {
                setCharCount(e.target.value.trim().length);
                setValue("description", e.target.value, {
                  shouldValidate: true,
                });
              },
            })}
          />
        </div>
        <div className="col-span-full" id="duration">
          <DurationPicker
            defaultDuration={defaultService?.duration}
            onDurationUpdatedHandler={(duration) =>
              onDurationUpdatedHandler(duration)
            }
          />
          {errors.duration && (
            <ErrorMessage message={errors.duration.message} />
          )}
        </div>
        <div className="col-span-full">
          <PriceInput
            label="Price"
            id="price"
            name="price"
            type="number"
            step="0.01"
            min="0"
            errormessage={errors?.price?.message}
            {...register("price", {
              required: "Price is required",
              valueAsNumber: true,
              validate: (value) =>
                value >= 0 || "Price cannot be less than 0",
            })}
          />
        </div>
        {/* Delivery type picker — sits directly after Price and before Photo
            so this structural choice and its dependent blocks (warnings, the
            travel-fee block) read as one cause→effect cluster. */}
        <div className="col-span-full" id="deliveryType">
          <SelectableCardGroup
            label="How is this service delivered?"
            name="deliveryType"
            options={DELIVERY_TYPE_OPTIONS}
            register={register}
            required="Please select a delivery type"
          />
          {errors.deliveryType && (
            <ErrorMessage message={errors.deliveryType.message} />
          )}
        </div>

        {/* Soft availability guard — non-blocking warnings. The fix stays
            inline in every common case: missing → in-drawer step swap to the
            LOCKED create form; turned off (any count) → the layered
            toggle-list ReenableAvailabilityModal — same experience as the
            services-page attention strip. */}
        {showMobileWarning && (
          <div className="col-span-full">
            <AlertYellow
              message={renderAvailabilityWarning({
                kind: "mobile",
                inactiveCount: inactiveMobileCount,
                onSetUp: () => setAvailStep("mobile"),
                onReenable: () => setReenableKind("mobile"),
              })}
            />
          </div>
        )}
        {showFixedWarning && (
          <div className="col-span-full">
            <AlertYellow
              message={renderAvailabilityWarning({
                kind: "fixed",
                inactiveCount: inactiveFixedCount,
                onSetUp: () => setAvailStep("fixed"),
                onReenable: () => setReenableKind("fixed"),
              })}
            />
          </div>
        )}

        {/* Inline travel-fee block — shown when delivery includes mobile.
            Saves through updateSettings (provider-level), NOT the service. */}
        {showTravelFeeBlock && (
          <div className="col-span-full rounded-card border border-ink-200 bg-cream-100 p-4 space-y-4">
            <div>
              <h4 className="text-sm font-medium text-ink-900">Travel fee</h4>
              <p className="mt-0.5 text-sm text-ink-500">
                This travel fee applies to all your mobile services. Updating it
                here changes it everywhere.
              </p>
            </div>
            <TravelFeeFields
              register={registerFee}
              watch={watchFee}
              setValue={setValueFee}
              selectedAddress={travelFeeAddress}
              onAddressSelect={handleFeeAddressSelect}
            />
          </div>
        )}

        <div className="col-span-full" id="image">
          <ImageUpload
            label="Photo"
            optional
            name="image"
            accept="image/*"
            defaulturl={defaultService?.imageUrl ?? null}
            filelist={imageFile}
            onRemoveHandler={onRemoveHandler}
            {...register("image")}
          />
          {errors.image && <ErrorMessage message={errors.image.message} />}
        </div>
      </div>
      <FormFooter
        primaryCaption={
          mode === FORM_MODE.CREATE ? "Create service" : "Save changes"
        }
        secondaryCaption="Cancel"
        secondaryClickHandler={onCancelHandler}
        isLoading={isSubmitting || isUpdatingSettings}
        requiresVerification={false}
      />
    </FormWrapper>

    <ReenableAvailabilityModal
      open={!!reenableKind}
      kind={reenableKind}
      availabilities={
        reenableKind === "mobile" ? inactiveMobileList : inactiveFixedList
      }
      onClose={() => setReenableKind(null)}
    />
    </>
  );
};

export default ServiceForm;
